import { useEffect, useMemo, useRef, useState } from "react";
import { GlBasemap } from "../lib/basemap";
import { Link, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, X, Storefront, NavigationArrow, MapTrifold, ListBullets, CalendarBlank, Crosshair, Flame, MagnifyingGlass, Sparkle, FunnelSimple, ArrowSquareOut, ShoppingBag } from "@phosphor-icons/react";
import api, { API, formatApiError } from "../lib/api";
import { track as trackAnalytics } from "../lib/analytics";
import { etTimeMs, fmtET, etDateKey, etDateKeyFromMs } from "../lib/etTime";
import PageMeta from "../components/PageMeta";

import PushOptInPrompt from "../components/PushOptInPrompt";
import ExpansionWelcome from "../components/ExpansionWelcome";
import CalendarSyncCard from "../components/CalendarSyncCard";
import { usePushSoftPrompt } from "../lib/usePushSoftPrompt";
import { FOOD_CATEGORIES, getCategoryOrDefault } from "../lib/foodCategories";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";
import { renderToStaticMarkup } from "react-dom/server";
import { MenuImagesCarousel, MenuImagesLightbox } from "../components/MenuImagesGallery";
import FollowButton from "../components/FollowButton";
import ShareButton from "../components/ShareButton";
import NotifyMeBell from "../components/NotifyMeBell";
import { getFollowerId, syncProfileFromServer, getFollowedIds } from "../lib/follows";
import { getNotifyStatus } from "../lib/push";
import { useAuth } from "../lib/auth";
import LoginPromptModal from "../components/LoginPromptModal";
import DateFilterPicker from "../components/DateFilterPicker";
import FiltersDrawer from "../components/FiltersDrawer";
import WeatherChip from "../components/WeatherChip";
import useWeatherForecasts from "../lib/useWeather";
import DirectionsButton from "../components/DirectionsButton";
import ContactTruckButton from "../components/ContactTruckButton";
import FoundingProBadge from "../components/dashboard/FoundingProBadge";
import FoundingCityBadge from "../components/FoundingCityBadge";
import HotspotSheet from "../components/HotspotSheet";
import { hotspotIcon } from "../components/HotspotIcon";
import { useFeatureFlags } from "../lib/featureFlags";

const FILTERS = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This Week" },
  { key: "next_week", label: "Next Week" },
  { key: "followed", label: "⭐ Your Trucks" },
];

const NEARBY_RADIUS_MILES = 10;

function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius in miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Memoize generated DivIcons so switching map filters doesn't thrash Leaflet
// into rebuilding identical markers. Keyed by
// `${categorySlug}|${status}|${boosted}|${logoUrl}` so the logo variant
// caches independently from the category fallback.
const _TRUCK_ICON_CACHE = new Map();

function truckIcon(categorySlug = "default", status = "future", boosted = false, logoUrl = null, logoPos = null) {
  // `logoPos` is `{x: 0-100, y: 0-100}` — applied as CSS object-position
  // so wide/tall logos aren't centered-cropped. Null means default 50/50.
  const px = logoPos && Number.isFinite(logoPos.x) ? logoPos.x : 50;
  const py = logoPos && Number.isFinite(logoPos.y) ? logoPos.y : 50;
  const cacheKey = `${categorySlug}|${status}|${boosted ? "b" : ""}|${logoUrl || ""}|${px}|${py}`;
  const cached = _TRUCK_ICON_CACHE.get(cacheKey);
  if (cached) return cached;

  const cat = getCategoryOrDefault(categorySlug);

  // ---------------------------------------------------------------------
  // Pro Logo Pin variant
  // ---------------------------------------------------------------------
  // Renders a standardized circular container with the truck's uploaded
  // logo centered inside. `object-fit: cover` keeps the logo proportional
  // and intelligently crops horizontal logos to the center.
  //
  // `onerror` swaps the image for a category-emoji fallback inline so a
  // broken/404 logo never leaves a blank pin on the map. This matches the
  // backend gate's "logo_path exists" check but adds runtime defense in
  // depth for cases where the file got deleted between request + load.
  if (logoUrl) {
    const fallbackEmoji = cat.emoji || "🚚";
    // We embed a single static <img> + a hidden fallback span. The
    // onerror handler swaps `display` so when the load fails, the emoji
    // takes over the same 38×38 footprint — no layout shift.
    //
    // NOTE: We build the marker HTML as a plain string (not JSX) because
    // React's `renderToStaticMarkup` silently DROPS event-handler
    // attributes like `onError`. The result was `<img src="..."/>`
    // with no `onerror` at all, so any 404/CORS image would leave the
    // browser's blue-`?` broken-image icon on the map instead of
    // falling back to the category emoji. Building the string by hand
    // preserves `onerror=` as a real HTML attribute the browser runs.
    //
    // XSS: `logoUrl` originates from our backend (never user-injected
    // as raw HTML), but we still escape the four sensitive chars in
    // case a URL contains a stray `"` or `&`.
    const escAttr = (s) => String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const pulseHtml = ((status === "live" || boosted) && status !== "past")
      ? `<span class="truck-pin-pulse status-${escAttr(status)}"></span>`
      : "";
    const boostedCls = boosted ? " is-boosted" : "";
    // Cancel badge lives on the WRAPPER (not `.truck-logo-pin`) so it
    // isn't clipped by the pin's `overflow:hidden` (required to keep the
    // logo <img> perfectly circular). Same shape + colour as the Free
    // pin's cancel badge so cancelled Pro pins scream "cancelled" just
    // as loudly as cancelled Free pins.
    const cancelBadgeHtml = status === "canceled"
      ? `<span class="truck-logo-pin-cancel-badge" aria-hidden="true">🚫</span>`
      : "";
    const html = (
      `<div class="truck-logo-pin-wrapper status-${escAttr(status)}${boostedCls}">` +
        pulseHtml +
        `<div class="truck-logo-pin status-${escAttr(status)}${boostedCls}">` +
          `<img src="${escAttr(logoUrl)}" alt="" loading="lazy" ` +
            `style="object-position:${px}% ${py}%" ` +
            `onerror="this.style.display='none';var s=this.nextElementSibling;if(s)s.style.display='flex';">` +
          `<span class="truck-logo-pin-fallback" aria-hidden="true" style="display:none">` +
            `${fallbackEmoji}` +
          `</span>` +
        `</div>` +
        // Downward-pointing "stem" — gives Pro pins the same teardrop
        // silhouette as Free pins so the visual language is consistent
        // across the map. Colour matches the pin's ring per status
        // (orange/green/red/gray/amber).
        `<span class="truck-logo-pin-stem" aria-hidden="true"></span>` +
        // PRO wordmark badge — lives on the WRAPPER (not inside
        // `.truck-logo-pin`) because the pin has `overflow:hidden` to
        // keep the logo <img> circular, which would otherwise clip the
        // badge. Same reasoning as the cancel badge below.
        `<span class="truck-logo-pin-badge" aria-hidden="true">PRO</span>` +
        cancelBadgeHtml +
      `</div>`
    );
    const icon = L.divIcon({
      html,
      className: "truck-marker truck-marker-logo",
      // 44px circle + 14px stem = 58px total. Anchor at the stem tip so
      // the point sits exactly on the GPS coordinate (same convention
      // as the Free teardrop pin).
      iconSize: [44, 58],
      iconAnchor: [22, 58],
    });
    _TRUCK_ICON_CACHE.set(cacheKey, icon);
    return icon;
  }
  // Render the category emoji when one is set; otherwise fall back to the
  // stylized truck glyph we've always used. Orange teardrop is the default
  // (future) state; `.status-live` paints it green, `.status-past` paints
  // it black so customers can see at a glance which trucks are currently
  // serving vs. already done for the day.
  const innerGlyph = cat.emoji ? (
    <span className="truck-pin-emoji" aria-hidden="true">{cat.emoji}</span>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 6h11v10H3zM14 9h4l3 3v4h-7z"
        stroke="white"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="7" cy="17" r="2" fill="white" />
      <circle cx="17" cy="17" r="2" fill="white" />
    </svg>
  );

  const pinCls = [
    "truck-pin",
    cat.emoji ? "has-emoji" : "",
    `status-${status}`,
    boosted ? "is-boosted" : "",
  ].filter(Boolean).join(" ");
  const html = renderToStaticMarkup(
    <div style={{ position: "relative" }}>
      {/* Pulse ring renders ONLY for live (open-now, free-for-all UX
          signal) OR boosted (Pro upsell signal). Future standard
          pins stay flat — no orange glow — so the visual hierarchy
          reads honestly across the map. */}
      {(status === "live" || boosted) && status !== "past" && (
        <span className={`truck-pin-pulse status-${status}`} />
      )}
      <div className={pinCls}>
        {innerGlyph}
      </div>
    </div>,
  );
  const icon = L.divIcon({
    html,
    className: "truck-marker",
    iconSize: [38, 38],
    iconAnchor: [19, 38],
  });
  _TRUCK_ICON_CACHE.set(cacheKey, icon);
  return icon;
}

function userIcon() {
  const html = renderToStaticMarkup(
    <div className="user-pin">
      <span className="user-pin-pulse" />
      <div className="user-pin-dot" />
    </div>,
  );
  return L.divIcon({
    html,
    className: "user-marker",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function ThemeTiles() {
  const { theme } = useTheme();
  const map = useMap();
  // Light uses CartoDB "voyager" — noticeably better road contrast + larger
  // labels than the minimal "light_all" style, which helps readability
  // without fighting the orange truck pins for attention. Dark keeps the
  // existing minimal dark style so pins still glow.
  // GL vector basemap (OpenFreeMap) — the old tilePane contrast boost
  // doesn't apply to the GL canvas, so it was removed with the raster layer.
  void map;
  return <GlBasemap dark={theme === "dark"} key={theme} />;
}

/**
 * Fly to a selected truck and offset slightly upward so the pin isn't hidden
 * behind the bottom detail sheet.
 */
function FlyToSelected({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    const currentZoom = map.getZoom();
    const zoom = Math.max(currentZoom, 15);
    map.flyTo([target.lat, target.lng], zoom, { duration: 0.6 });
    // Nudge the pin up above the detail sheet after the fly settles.
    const t = setTimeout(() => {
      try {
        map.panBy([0, -110], { animate: true, duration: 0.3 });
      } catch {}
    }, 650);
    return () => clearTimeout(t);
  }, [target, map]);
  return null;
}

/**
 * On the first location lock, fly to the user with a subtle zoom-in.
 * `skip` lets the parent suppress this behaviour (e.g. when arriving via
 * a `?truck=<id>` deep link — in that case `FlyToSelected` owns the
 * camera and we don't want to yank the viewport back to the user).
 */
function InitialUserRecenter({ userPos, onReady, skip }) {
  const map = useMap();
  const didRef = useRef(false);
  useEffect(() => {
    if (skip || !userPos || didRef.current) return;
    didRef.current = true;
    map.flyTo([userPos.lat, userPos.lng], 13, { duration: 1.0 });
    onReady?.(map);
  }, [userPos, map, onReady, skip]);
  return null;
}

/**
 * Captures the Leaflet map instance so the recenter button (rendered outside
 * the MapContainer tree) can trigger `flyTo`.
 */
function MapCapture({ onMap }) {
  const map = useMap();
  useEffect(() => {
    onMap(map);
    return () => onMap(null);
  }, [map, onMap]);
  return null;
}

/**
 * Reports live map zoom to the parent so we can decide hotspot ↔ pin
 * transitions at zoom < 14. Stays tiny — no rerenders of the map itself.
 */
function ZoomWatcher({ onZoom }) {
  const map = useMap();
  useEffect(() => {
    onZoom(map.getZoom());
    const handler = () => onZoom(map.getZoom());
    map.on("zoomend", handler);
    return () => map.off("zoomend", handler);
  }, [map, onZoom]);
  return null;
}

function timeStr(iso) {
  return fmtET(iso, { hour: "numeric", minute: "2-digit" }) || iso;
}

function dayLabel(iso) {
  const key = etDateKey(iso);
  const nowMs = Date.now();
  if (key === etDateKeyFromMs(nowMs)) return "Today";
  if (key === etDateKeyFromMs(nowMs, 1)) return "Tomorrow";
  return fmtET(iso, { weekday: "short", month: "short", day: "numeric" });
}

function NearMeButton({ userPos, value, onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  // Close the popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const disabled = !userPos;
  const active = value != null;
  const options = [
    { mi: 1, label: "1 mi" },
    { mi: 3, label: "3 mi" },
    { mi: 5, label: "5 mi" },
    { mi: 10, label: "10 mi" },
    { mi: 25, label: "25 mi" },
  ];

  return (
    <div className="pointer-events-auto relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((s) => !s)}
        disabled={disabled}
        title={disabled ? "Enable location to filter by distance" : "Filter by distance from you"}
        className={
          "h-9 px-3 rounded-full border shadow-sm inline-flex items-center gap-1.5 text-sm transition whitespace-nowrap " +
          (active
            ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
            : "bg-card/90 backdrop-blur-xl border-border text-foreground hover:bg-muted") +
          (disabled ? " opacity-50 cursor-not-allowed" : "")
        }
        data-testid="near-me-btn"
      >
        <Crosshair weight={active ? "fill" : "duotone"} className="h-4 w-4" />
        <span className="font-medium">
          {active ? `Within ${value} mi` : "Near me"}
        </span>
        {active && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="ml-1 h-5 w-5 grid place-items-center rounded-full hover:bg-white/20"
            role="button"
            aria-label="Clear distance filter"
            data-testid="near-me-clear"
          >
            <X className="h-3 w-3" />
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute z-20 mt-2 right-0 sm:left-0 sm:right-auto min-w-[180px] rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-lg p-1"
          data-testid="near-me-popover"
        >
          {options.map((o) => (
            <button
              key={o.mi}
              type="button"
              onClick={() => { onChange(o.mi); setOpen(false); }}
              className={
                "w-full text-left px-3 h-9 rounded-xl text-sm transition " +
                (value === o.mi
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-foreground hover:bg-muted")
              }
              data-testid={`near-me-option-${o.mi}`}
            >
              Within {o.label}
            </button>
          ))}
          {value != null && (
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); }}
              className="w-full text-left px-3 h-9 rounded-xl text-sm text-muted-foreground hover:bg-muted border-t border-border mt-1"
              data-testid="near-me-option-off"
            >
              Turn off
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryChip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "shrink-0 h-8 px-3 rounded-full text-xs font-medium transition whitespace-nowrap " +
        (active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-muted")
      }
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function TruckCard({ truck: t, openNow, distanceMi, onClick, isNotifySubscribed, onNotifyChange, weather }) {
  const eventSub = t.event_name?.trim();
  // Pretty label for the cancellation reason — falls back to the
  // custom reason text when the owner picked "Other".
  const cancelReasonLabel = (() => {
    if (!t.is_canceled) return null;
    const map = {
      weather: "Weather",
      mechanical: "Mechanical Issue",
      staffing: "Staffing Shortage",
      organizer: "Event Organizer Canceled",
      personal: "Personal Emergency",
    };
    if (t.cancel_reason === "other") return t.cancel_reason_custom || "Other";
    return map[t.cancel_reason] || null;
  })();
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`w-full text-left rounded-2xl border bg-card p-5 sm:p-6 hover:border-primary/50 transition cursor-pointer ${
        t.is_canceled
          ? "border-rose-300 ring-1 ring-rose-200 bg-rose-50/40 dark:bg-rose-950/20"
          : openNow ? "border-primary/60 ring-1 ring-primary/20" : "border-border"
      }`}
      data-testid={`list-truck-${t.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-[1.05] text-foreground truncate">
              {t.truck_name}
            </h3>
            {t.founding_city_slug && t.founding_slot_number ? (
              <FoundingCityBadge
                citySlug={t.founding_city_slug}
                slotNumber={t.founding_slot_number}
                size="sm"
              />
            ) : t.owner_is_founding_pro && (
              <FoundingProBadge size="sm" />
            )}
            {t.is_boosted && (
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white bg-amber-500 rounded-full px-2.5 py-1 font-semibold"
                data-testid="boosted-chip"
                title="Featured boost"
              >
                ⚡ Boosted
              </span>
            )}
            {t.is_trending && (
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white bg-gradient-to-r from-rose-500 to-orange-500 rounded-full px-2.5 py-1 font-bold shadow-sm"
                data-testid="trending-chip"
                title="Trending — getting lots of shares this week"
              >
                🔥 Trending
              </span>
            )}
            {t.owner_is_pro && (
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5 font-bold"
                data-testid="pro-chip"
                title="TruckTrackr Pro"
              >
                Pro
              </span>
            )}
            {t.is_canceled ? (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white bg-rose-600 rounded-full px-2.5 py-1 font-bold"
                data-testid="canceled-chip"
                title={cancelReasonLabel ? `Canceled — ${cancelReasonLabel}` : "Event canceled"}
              >
                🚫 Canceled
              </span>
            ) : openNow ? (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary-foreground bg-primary rounded-full px-2.5 py-1 font-semibold"
                data-testid="open-now-chip"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Open now
              </span>
            ) : (
              <span className="text-[11px] uppercase tracking-[0.18em] text-primary bg-primary/10 rounded-full px-2.5 py-1 font-semibold">
                {dayLabel(t.starts_at)}
              </span>
            )}
            <FollowButton
              truckId={t.truck_id || t.id}
              truckName={t.truck_name}
              compact
            />
            <NotifyMeBell
              truckId={t.truck_id || t.id}
              truckName={t.truck_name}
              subscribed={!!isNotifySubscribed}
              onChange={(next) => onNotifyChange?.(t.truck_id || t.id, next)}
              compact
            />
          </div>
          {eventSub && (
            <div className="mt-1.5 text-lg font-semibold text-foreground/90 leading-snug">{eventSub}</div>
          )}
          <div className="mt-4 text-lg font-semibold text-foreground flex items-center gap-2">
            <Clock weight="fill" className="h-5 w-5 text-primary flex-none" />
            {timeStr(t.starts_at)} — {timeStr(t.ends_at)}
          </div>
          <div className="mt-2.5 text-base text-foreground/80 flex items-start gap-2 flex-wrap">
            <span className="flex items-start gap-2 min-w-0">
              <MapPin weight="fill" className="h-5 w-5 mt-0.5 flex-none text-primary" />
              <span className="line-clamp-2 leading-snug">{t.address}</span>
            </span>
            {distanceMi != null && (
              <span
                className="inline-flex items-center gap-1 text-[12px] text-foreground/70 bg-muted rounded-full px-2.5 py-1 font-semibold tabular-nums flex-none"
                data-testid={`distance-chip-${t.id}`}
              >
                {distanceMi.toFixed(1)} mi away
              </span>
            )}
          </div>
          {t.truck_slug && (
            <div className="mt-3 flex">
              <Link
                to={`/t/${t.truck_slug}`}
                onClick={(e) => {
                  // Don't bubble up to the card's onClick (which opens
                  // the bottom sheet) — clicking "View profile" should
                  // navigate, not also surface the truck sheet.
                  e.stopPropagation();
                  trackAnalytics("truck_profile_cta_click", t.truck_id || t.id, {
                    truck_name: t.truck_name,
                    source_card_type: "list_card",
                    is_pro: !!t.owner_is_pro,
                  });
                }}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-border text-xs font-semibold text-foreground hover:bg-muted transition-colors"
                data-testid={`list-truck-${t.id}-view-profile`}
              >
                <Storefront weight="duotone" className="h-3.5 w-3.5 text-primary" />
                View profile
                <ArrowSquareOut className="h-3 w-3 opacity-60" />
              </Link>
            </div>
          )}
        </div>
        {(() => {
          const thumb = (Array.isArray(t.menu_image_paths) && t.menu_image_paths[0]) || t.menu_image_path;
          if (!thumb) return null;
          return (
            <img
              src={`${API}/files/${thumb}`}
              alt=""
              loading="lazy"
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl object-cover flex-none"
            />
          );
        })()}
      </div>
    </div>
  );
}

function ListSection({ title, trucks, onSelect, isOpenNow, distanceFor, emptyHint, accent, testId, notifyTrucks, onNotifyChange, weatherById }) {
  return (
    <section data-testid={testId}>
      <h2
        className={`font-display font-semibold text-sm uppercase tracking-[0.18em] mb-3 flex items-center gap-2 ${
          accent ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {accent && <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />}
        {title}
        <span className="font-normal text-xs text-muted-foreground normal-case tracking-normal">
          ({trucks.length})
        </span>
      </h2>
      {trucks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {emptyHint}
        </div>
      ) : (
        <div className="space-y-3">
          {trucks.map((t) => (
            <TruckCard
              key={t.id}
              truck={t}
              openNow={isOpenNow(t)}
              distanceMi={distanceFor?.(t)}
              onClick={() => onSelect(t)}
              isNotifySubscribed={notifyTrucks?.has?.(t.truck_id || t.id)}
              onNotifyChange={onNotifyChange}
              weather={weatherById?.get?.(t.truck_id || t.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function MapView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTruckId = searchParams.get("truck");
  const [filter, setFilter] = useState(requestedTruckId ? "week" : "today");
  // `?view=list` from the landing-page "Live hours" card jumps customers
  // straight into the list view on first paint. We ALSO write the URL
  // back when the user toggles modes, so that:
  //   1. Tapping a truck → profile → browser-back returns to the same
  //      view they were on (list stays list, map stays map).
  //   2. Sharing the URL preserves the mode.
  const [viewMode, setViewModeState] = useState(
    () => (searchParams.get("view") === "list" ? "list" : "map")
  );
  const setViewMode = (next) => {
    setViewModeState(next);
    // Only write to URL when it actually differs — avoids history spam
    // and prevents a no-op `replace` from clobbering other query params.
    const current = searchParams.get("view");
    const desired = next === "list" ? "list" : null;
    if (current === desired) return;
    const sp = new URLSearchParams(searchParams);
    if (desired) sp.set("view", desired);
    else sp.delete("view");
    setSearchParams(sp, { replace: true });
  };
  const [sortMode, setSortMode] = useState("time"); // "time" | "distance"
  // Category chip filter — "" means "all". Search is a lowercase substring
  // match against truck_name.
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // "Near me" distance filter — miles, null = off. Only applied when we
  // have a user position (the toggle button is disabled otherwise so this
  // stays in sync with reality).
  const [maxDistanceMi, setMaxDistanceMi] = useState(null);
  // Hide past-ended events. Defaults to **ON** for new visitors so the map
  // only shows live + upcoming — ended (black) pins add noise and can't be
  // acted on. Returning users who explicitly turned it OFF are respected
  // via the "0" stored value (null = first visit = ON).
  const [hidePast, setHidePast] = useState(() => {
    try {
      const stored = localStorage.getItem("tt_hide_past");
      if (stored === null) return true;
      return stored === "1";
    } catch {
      return true;
    }
  });
  // Filters drawer is the new home for the secondary filter controls
  // (search / distance / hide-past / categories). Keeps the floating
  // header to a single mobile-friendly row.
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem("tt_hide_past", hidePast ? "1" : "0"); } catch {}
  }, [hidePast]);
  // Pin-color legend — shown by default for new users; dismissible for
  // repeat visitors who've learned the code. Small "Key" pill appears
  // bottom-left after dismissal to re-open it anytime.
  const [legendDismissed, setLegendDismissed] = useState(() => {
    try { return localStorage.getItem("tt_legend_dismissed") === "1"; } catch { return false; }
  });
  const [tickMs, setTickMs] = useState(() => Date.now());
  // Tick every 30s so live → past transitions reflect without a refresh.
  // 30s feels real-time enough without thrashing Leaflet.
  useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const now = tickMs;
  const timeStatus = (t) => {
    const e = etTimeMs(t.ends_at);
    if (now >= e) return "past";
    // Canceled events stay on the map until ends_at painted red — they
    // outrank live/future so the pin colour signals the cancellation
    // first, not "open now" green. After ends_at the normal cleanup
    // kicks in via the "past" branch above.
    if (t.is_canceled) return "canceled";
    const s = etTimeMs(t.starts_at);
    if (now >= s) return "live";
    return "future";
  };
  const isOpenNow = (t) => timeStatus(t) === "live";
  const [trucks, setTrucks] = useState([]);
  const [hotspots, setHotspots] = useState([]); // [{id, lat, lng, count, tier, trucks:[]}]
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  // Current map zoom — drives hotspot ↔ pin transition at zoom < 14.
  const [mapZoom, setMapZoom] = useState(null);
  const { flags: publicFlags } = useFeatureFlags();
  const hotspotsEnabled = publicFlags?.enable_hotspots !== false; // default ON
  const hotspotAnimEnabled = publicFlags?.enable_hotspot_animations !== false;
  // Hotspot ↔ individual-pin transition threshold. < HOTSPOT_ZOOM_THRESHOLD
  // shows the 🔥 marker; ≥ reveals the underlying jittered pins so users
  // can tap them directly.
  const HOTSPOT_ZOOM_THRESHOLD = 14;
  const [loading, setLoading] = useState(true);
  // True when the truck feed itself failed (server blip) — renders a
  // "couldn't load" + Retry state instead of the misleading "No trucks
  // posted yet" empty state. `reloadKey` re-runs the feed effect.
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { paths, startIndex } or null
  const [mapInstance, setMapInstance] = useState(null);
  // Active-truck count from /api/trucks/active-count — drives the
  // "See all active trucks (N)" toolbar CTA. Loaded once per mount.
  const [activeProfileCount, setActiveProfileCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/trucks/active-count")
      .then(({ data }) => {
        if (!cancelled && typeof data?.active === "number") {
          setActiveProfileCount(data.active);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Icons are generated on-the-fly via `truckIcon(slug, status)` — cached
  // per (category, status) pair so Leaflet doesn't rebuild identical markers.
  const uIcon = useMemo(() => userIcon(), []);

  const LANCASTER_COUNTY = { lat: 40.0379, lng: -76.3055 };
  const DEFAULT_ZOOM = 10;

  // Ask geolocation on mount — but ONLY return a position if the browser
  // already granted it. Never auto-triggers the OS prompt; the user must
  // tap the recenter ("Use my location") button for that. This fixes the
  // repeated-prompt bug where every map re-render re-asked the user.
  useEffect(() => {
    let cancelled = false;
    import("../lib/geo").then(({ getUserLocation }) => {
      getUserLocation({ requireInteraction: false }).then((res) => {
        if (cancelled) return;
        if (res.ok) setUserPos({ lat: res.lat, lng: res.lng });
      });
    });
    return () => { cancelled = true; };
  }, []);

  // Load trucks whenever filter changes
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setSelectedHotspot(null);
    // Clear any previously-opened truck detail — otherwise switching filters
    // (e.g. "This Week" → "Tomorrow") leaves a pin detail from the old list
    // visible, making it look like the new filter includes that truck.
    setSelected(null);
    // Followed-trucks view taps a different endpoint and reconciles the
    // local set from the server first so stale follows don't show up.
    const fetcher =
      filter === "followed"
        ? syncProfileFromServer().then(() =>
            api.get("/follows/trucks", {
              params: { follower_id: getFollowerId() },
            })
          )
        : api.get("/trucks/upcoming", { params: { when: filter } });
    Promise.resolve(fetcher)
      .then(({ data }) => {
        if (mounted) {
          setTrucks(data);
          setLoadError(false);
        }
      })
      .catch((e) => {
        if (!mounted) return;
        setLoadError(true);
        // Only toast real client errors (4xx). Server/infra blips render
        // the inline retry card instead — a raw Cloudflare sentence in a
        // toast scared users during the Mar 2026 production incident.
        const status = e?.response?.status;
        if (status && status < 500) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      })
      .finally(() => mounted && setLoading(false));
    // Hotspots — fetched in parallel for the customer-facing windows
    // only (followed-view is a personal list, not a public gathering).
    if (filter !== "followed") {
      api.get("/trucks/hotspots", { params: { when: filter } })
        .then(({ data }) => {
          if (mounted) setHotspots(Array.isArray(data?.hotspots) ? data.hotspots : []);
        })
        .catch(() => { if (mounted) setHotspots([]); });
    } else {
      setHotspots([]);
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, reloadKey]);

  // Also clear the selected truck if a refresh removed it from the list.
  useEffect(() => {
    if (!selected) return;
    if (!trucks.some((t) => t.id === selected.id)) {
      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trucks]);

  // Honor ?truck=<id> from the landing's "View truck" CTA. A single
  // truck can appear multiple times (one entry per day across the
  // week's schedule), so we can't just pick `find` — we need the one
  // that's relevant *now*:
  //   1. Live now (started, not yet ended)
  //   2. Next upcoming (starts_at in the future, earliest first)
  //   3. Fallback: the first match (past events — shouldn't happen if
  //      filter is "Today"/"This Week", but guards against a stale ID
  //      whose only event already wrapped).
  // Then drop the query param so back-navigation doesn't re-trigger.
  useEffect(() => {
    if (!requestedTruckId || trucks.length === 0) return;
    const matches = trucks.filter(
      (t) => t.id === requestedTruckId || t.truck_id === requestedTruckId
    );
    if (matches.length === 0) return;
    const now = Date.now();
    const parseT = (s) => {
      const t = etTimeMs(s);
      return Number.isNaN(t) ? null : t;
    };
    const live = matches.find((t) => {
      const s = parseT(t.starts_at);
      const e = parseT(t.ends_at);
      return s != null && e != null && s <= now && now <= e;
    });
    const future = matches
      .filter((t) => {
        const s = parseT(t.starts_at);
        return s != null && s > now;
      })
      .sort((a, b) => parseT(a.starts_at) - parseT(b.starts_at))[0];
    const hit = live || future || matches[0];
    setSelected(hit);
    searchParams.delete("truck");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trucks, requestedTruckId]);

  // Track a "profile_view" whenever the customer opens a truck's detail. The
  // backend dedupes per (truck_id, event, session, day) so rapid open/close
  // toggles don't inflate numbers.
  useEffect(() => {
    if (!selected) return;
    const tid = selected.truck_id || selected.id;
    if (tid) trackAnalytics("profile_view", tid);
  }, [selected]);

  const sortedTrucks = useMemo(
    () => {
      // Apply category + name + distance filters FIRST so the sort runs on
      // the smaller filtered set. Every downstream consumer reads this memo.
      let arr = trucks;
      if (categoryFilter) {
        arr = arr.filter((t) => (t.food_category || "default") === categoryFilter);
      }
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        arr = arr.filter((t) => (t.truck_name || "").toLowerCase().includes(q));
      }
      if (maxDistanceMi != null && userPos) {
        arr = arr.filter((t) => {
          const d = distanceMiles(userPos.lat, userPos.lng, t.lat, t.lng);
          return d <= maxDistanceMi;
        });
      }
      if (hidePast) {
        // 2-hour grace: keep "Ended" (black) pins around for 2 hours after
        // close so a customer who just missed a truck can still see "they
        // were just here". After 2 hours, drop them entirely so the map
        // stops accumulating noise from earlier-in-the-day events.
        // Uses ET-aware parsing (etTimeMs) so naive schedule timestamps
        // are interpreted as Lancaster local time, not the user's device
        // local time. Backend applies the same cutoff in `_trucks_for_window`.
        const cutoff = now - 2 * 60 * 60 * 1000;
        arr = arr.filter((t) => etTimeMs(t.ends_at) > cutoff);
      }
      // Sort: boosted first (Pro feature priority); trending next (social
      // proof — high-share trucks float toward the top so customers see
      // what's hot). In the "Your Trucks" view we then push LIVE events
      // ahead of upcoming. Finally tie-break by starts_at (soonest first).
      const liveFirst = filter === "followed";
      return [...arr].sort((a, b) => {
        if (!!a.is_boosted !== !!b.is_boosted) return a.is_boosted ? -1 : 1;
        if (!!a.is_trending !== !!b.is_trending) return a.is_trending ? -1 : 1;
        if (liveFirst) {
          const la = isOpenNow(a) ? 0 : 1;
          const lb = isOpenNow(b) ? 0 : 1;
          if (la !== lb) return la - lb;
        }
        return etTimeMs(a.starts_at) - etTimeMs(b.starts_at);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trucks, categoryFilter, searchQuery, maxDistanceMi, userPos, hidePast, now, filter],
  );

  // Count of trucks currently open (live) across the filtered set. Shown in
  // the header as a "N live" chip so customers get a one-glance answer to
  // "is anywhere actually open right now?" without scanning the map.
  const liveCount = useMemo(
    () => sortedTrucks.filter((t) => isOpenNow(t)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedTrucks, now],
  );

  // Pin impression tracking — fire once per truck per session/day as
  // soon as the pin is part of the currently visible (filtered) set.
  // Backend has a unique (truck, event_type, session_key, date) index
  // so the upsert naturally dedups; we also keep a client-side ref so
  // we don't waste fetch keepalive slots re-firing as filters change.
  // Placed AFTER sortedTrucks declaration — referencing it before would
  // throw "Cannot access 'sortedTrucks' before initialization" (TDZ).
  const impressionsSentRef = useRef(new Set());
  useEffect(() => {
    if (!sortedTrucks || sortedTrucks.length === 0) return;
    // Only track CURRENTLY VIEWABLE trucks (live + upcoming). Past pins
    // shown via 2h grace are not "impressions" worth counting.
    for (const t of sortedTrucks) {
      const tid = t.truck_id || t.id;
      if (!tid) continue;
      if (impressionsSentRef.current.has(tid)) continue;
      const status = timeStatus(t);
      if (status === "past") continue;
      impressionsSentRef.current.add(tid);
      trackAnalytics("pin_impression", tid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTrucks]);

  // Category usage heuristic — don't surface filters that would return
  // empty or near-empty results. A category is "viable" only if at least
  // MIN_TRUCKS_PER_CAT trucks in the current (pre-category-filter) dataset
  // use it. The whole bar stays hidden until we have >= 2 viable categories,
  // so customers never see a stranded "All" pill with no real chips.
  const MIN_TRUCKS_PER_CAT = 3;
  const categoryMeta = useMemo(() => {
    // Count categories across the un-category-filtered dataset (so that
    // picking a category doesn't hide all the OTHER categories). Respects
    // every other filter (search, distance, hide-past, today/week window).
    let source = [...trucks];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      source = source.filter((t) => (t.truck_name || "").toLowerCase().includes(q));
    }
    if (maxDistanceMi != null && userPos) {
      source = source.filter((t) => distanceMiles(userPos.lat, userPos.lng, t.lat, t.lng) <= maxDistanceMi);
    }
    if (hidePast) {
      const cutoff = now - 2 * 60 * 60 * 1000;
      source = source.filter((t) => etTimeMs(t.ends_at) > cutoff);
    }
    const counts = new Map();
    for (const t of source) {
      const slug = (t.food_category || "default");
      if (slug === "default") continue;
      counts.set(slug, (counts.get(slug) || 0) + 1);
    }
    const viable = FOOD_CATEGORIES.filter(
      (c) => c.slug !== "default" && (counts.get(c.slug) || 0) >= MIN_TRUCKS_PER_CAT
    );
    const totalCategorized = Array.from(counts.values()).reduce((a, b) => a + b, 0);
    const share = source.length ? totalCategorized / source.length : 0;
    // Spec: show bar only when there's enough density that real chips will
    // appear — at least 2 categories that each have ≥ MIN_TRUCKS_PER_CAT
    // trucks. The previous "share-only" branch could surface the bar with
    // just an "All" chip and no useful filters, which is exactly the
    // "weak filter experience" we wanted to avoid (user feedback: mobile
    // map showed a stranded "All" pill).
    // Edge case: if a user has a chip selected (even one that fell below
    // threshold), keep the bar visible so they can always unselect.
    const shouldShow = categoryFilter !== "" || viable.length >= 2;
    return { counts, viable, share, shouldShow };
  }, [trucks, searchQuery, maxDistanceMi, userPos, hidePast, now, categoryFilter]);

  // Tiny coordinate jitter so multiple events at the same location don't stack
  // into a single pin on the map. Jitter radius widens with count so 10+
  // trucks at the same spot still remain individually clickable.
  const mapPins = useMemo(() => {
    const counts = {};
    return sortedTrucks.map((t) => {
      const key = `${t.lat.toFixed(5)}|${t.lng.toFixed(5)}`;
      counts[key] = (counts[key] || 0) + 1;
      const n = counts[key] - 1;
      if (n === 0) return { ...t, _lat: t.lat, _lng: t.lng };
      // Spiral outward: each ring of 8 spreads further. ~13m base radius
      // expanding to ~55m for the 17th+ pin at the same spot.
      const ring = Math.floor(n / 8) + 1;
      const angle = ((n % 8) * 2 * Math.PI) / 8;
      const r = 0.00012 * ring; // 13m per ring
      return {
        ...t,
        _lat: t.lat + r * Math.cos(angle),
        _lng: t.lng + r * Math.sin(angle),
      };
    });
  }, [sortedTrucks]);

  // ---- Smart Event Hotspots --------------------------------------
  // At map zoom < HOTSPOT_ZOOM_THRESHOLD we want hotspot 🔥 markers to
  // represent gatherings. Above that, the user has zoomed in close
  // enough that individual pins read clearly — collapse the hotspot
  // and let the underlying pins shine. Filtered datasets (search,
  // category, near-me) re-narrow the hotspot to only the trucks the
  // user is currently looking at; if fewer than min remain we hide
  // the hotspot entirely so we never mislead the user.
  const HOTSPOT_MIN_VISIBLE = 3;
  const activeHotspots = useMemo(() => {
    if (!hotspotsEnabled || !hotspots.length) return [];
    if (mapZoom !== null && mapZoom >= HOTSPOT_ZOOM_THRESHOLD) return [];
    const visibleIds = new Set(sortedTrucks.map((t) => t.id));
    const narrowed = [];
    for (const h of hotspots) {
      const ids = (h.truck_ids || []).filter((id) => visibleIds.has(id));
      if (ids.length < HOTSPOT_MIN_VISIBLE) continue;
      // Use the original full preview for the sheet (gives the user the
      // full event picture even if a filter narrowed the map). For the
      // marker itself we use the narrowed count so the 🔥 number aligns
      // with what the user currently sees.
      narrowed.push({
        ...h,
        visible_count: ids.length,
        truck_ids_visible: ids,
      });
    }
    return narrowed;
  }, [hotspotsEnabled, hotspots, mapZoom, sortedTrucks]);

  // Set of truck ids to SUPPRESS on the map (rendered inside a hotspot).
  const suppressedTruckIds = useMemo(() => {
    const s = new Set();
    for (const h of activeHotspots) {
      for (const id of (h.truck_ids_visible || h.truck_ids || [])) s.add(id);
    }
    return s;
  }, [activeHotspots]);

  // Track impressions per hotspot id once per filter swap so we don't
  // double-count rapid-fire re-renders.
  const seenHotspotImpressions = useRef(new Set());
  useEffect(() => {
    // Reset on filter change.
    seenHotspotImpressions.current = new Set();
  }, [filter]);
  useEffect(() => {
    for (const h of activeHotspots) {
      if (seenHotspotImpressions.current.has(h.id)) continue;
      seenHotspotImpressions.current.add(h.id);
      trackAnalytics("hotspot_view", h.id);
    }
  }, [activeHotspots]);

  const { openNow, laterToday } = useMemo(() => {
    if (filter !== "today") return { openNow: [], laterToday: [] };
    const open = [];
    const later = [];
    for (const t of sortedTrucks) {
      if (isOpenNow(t)) open.push(t);
      else if (etTimeMs(t.starts_at) > now) later.push(t);
    }    return { openNow: open, laterToday: later };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTrucks, filter, now]);

  // Nearby counts (within ~10 miles of the user). We don't show the radius in
  // the UI — just the resulting counts.
  const { openNowCount, thisWeekCount } = useMemo(() => {
    if (!userPos) return { openNowCount: 0, thisWeekCount: 0 };
    let onc = 0;
    let twc = 0;
    for (const t of sortedTrucks) {
      const d = distanceMiles(userPos.lat, userPos.lng, t.lat, t.lng);
      if (d > NEARBY_RADIUS_MILES) continue;
      twc += 1;
      if (isOpenNow(t)) onc += 1;
    }
    return { openNowCount: onc, thisWeekCount: twc };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTrucks, userPos, now]);

  const recenterOnUser = async () => {
    // If we already have userPos (browser granted earlier), just fly to it.
    if (userPos && mapInstance) {
      mapInstance.flyTo([userPos.lat, userPos.lng], 13, { duration: 0.7 });
      return;
    }
    // No position cached — this is an explicit user action ("Use my
    // location" tap), so we MAY trigger the OS prompt. Geo helper
    // dedupes concurrent requests + caches denials for 24h.
    const { getUserLocation } = await import("../lib/geo");
    const res = await getUserLocation({ requireInteraction: true });
    if (res.ok) {
      setUserPos({ lat: res.lat, lng: res.lng });
      if (mapInstance) mapInstance.flyTo([res.lat, res.lng], 13, { duration: 0.7 });
    } else if (res.reason === "denied") {
      toast.error("Location access is turned off. Enable it in browser settings to see trucks near you.");
    } else if (res.reason === "timeout") {
      toast.error("Couldn't get your location right now. You can still browse by map or list.");
    } else if (res.reason === "unsupported") {
      toast.error("Your browser doesn't support location. Browse by map or list instead.");
    }
  };

  // Phase B push opt-in soft prompt — fires after 20s OR 2 meaningful
  // interactions, whichever first. Auto-suppressed when:
  //   - browser doesn't support push
  //   - permission already granted/denied
  //   - "Not now" was clicked in the last 7 days
  //   - prompt already shown this session
  // We hide it whenever a truck detail sheet is open so it never
  // overlaps the primary action.
  const pushPrompt = usePushSoftPrompt();
  const promptVisible = pushPrompt.visible && !selected;

  // Contextual login nudge — fires once per tab session when an
  // anonymous user with at least one follow opens the map and one of
  // their followed trucks is currently LIVE. Copy names the truck
  // ("Bob's Burgers just opened — sign in to get notified next time")
  // which is far more emotionally resonant than the generic 3rd-follow
  // "save your favorites" pitch.
  //
  // Shares the same sessionStorage flag as FollowButton's 3rd-follow
  // nudge so the user never sees both prompts in a single session.
  const { user } = useAuth();
  const [contextualSigninTruck, setContextualSigninTruck] = useState(null);
  const _contextualFiredRef = useRef(false);

  useEffect(() => {
    if (_contextualFiredRef.current) return;
    if (user) return;                              // logged in → never nudge
    if (loading || trucks.length === 0) return;    // wait for first data load
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem("tt:follow:signin-nudge-shown") === "1"; } catch {}
    if (alreadyShown) {
      _contextualFiredRef.current = true;
      return;
    }
    const followedIds = new Set(getFollowedIds());
    if (followedIds.size === 0) return;            // not a fan of anyone yet
    // Find the FIRST followed truck that's currently live. The truck
    // identifier in our cached list is `id` (also exposed as `truck_id`
    // on some payloads) — check both for safety.
    const liveFollowed = trucks.find(
      (t) => isOpenNow(t) && (followedIds.has(t.id) || followedIds.has(t.truck_id)),
    );
    if (!liveFollowed) return;
    _contextualFiredRef.current = true;
    try { sessionStorage.setItem("tt:follow:signin-nudge-shown", "1"); } catch {}
    setContextualSigninTruck(liveFollowed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, trucks]);

  // Bumps the soft-prompt interaction counter whenever a truck detail
  // sheet opens (regardless of how it was triggered: pin click, list
  // card click, or ?truck= deep link). markInteraction is internally
  // idempotent past the threshold so over-counting is harmless.
  useEffect(() => {
    if (selected) pushPrompt.markInteraction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Follower count for the date picker's "Your trucks" chip (so the
  // chip hides when the visitor has no follows yet).
  const [followedCount, setFollowedCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    syncProfileFromServer().then(() => {
      if (mounted) setFollowedCount(getFollowedIds().size);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Set of truck_ids this visitor will receive truck_live pings for.
  // Mirrors the backend (`/push/notify-status`) on mount + after every
  // bell flip so all bells across the page stay in sync.
  const [notifyTrucks, setNotifyTrucks] = useState(() => new Set());
  useEffect(() => {
    let mounted = true;
    getNotifyStatus().then((s) => {
      if (mounted) setNotifyTrucks(new Set(s.truckIds || []));
    });
    return () => { mounted = false; };
  }, []);
  const setNotifyForTruck = (truckId, on) => {
    setNotifyTrucks((prev) => {
      const next = new Set(prev);
      if (on) next.add(truckId);
      else next.delete(truckId);
      return next;
    });
  };
  // Listen for cross-component events from FollowButton's "Notify me"
  // toast so other bells on the page flip in sync without a refetch.
  useEffect(() => {
    const onSub = (e) => {
      const id = e?.detail?.truckId;
      if (id) setNotifyForTruck(id, true);
    };
    window.addEventListener("tt:notify-subscribed", onSub);
    return () => window.removeEventListener("tt:notify-subscribed", onSub);
  }, []);

  // Count of secondary filters currently engaged — drives the dot/badge
  // on the funnel button. We treat hidePast=true as the default state so
  // it doesn't add to the count (otherwise the badge is always >0).
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (searchQuery.trim()) n++;
    if (maxDistanceMi != null) n++;
    if (!hidePast) n++;
    if (categoryFilter) n++;
    if (filter === "followed") n++;
    return n;
  }, [searchQuery, maxDistanceMi, hidePast, categoryFilter, filter]);

  // Compute miles from the user to a given truck (null when no geolocation)
  const distanceFor = useMemo(() => {
    if (!userPos) return () => null;
    return (t) => distanceMiles(userPos.lat, userPos.lng, t.lat, t.lng);
  }, [userPos]);

  // Out-of-market detection (Aug 2026 expansion experience): the viewer has
  // location on and the NEAREST truck is 40+ miles away — show the
  // "coming to your city" card above the list instead of a confusing feed
  // of far-away events.
  const nearestTruckMi = useMemo(() => {
    if (!userPos || trucks.length === 0) return null;
    let best = Infinity;
    for (const t of trucks) {
      if (t.lat == null || t.lng == null) continue;
      const d = distanceMiles(userPos.lat, userPos.lng, t.lat, t.lng);
      if (d < best) best = d;
    }
    return Number.isFinite(best) ? best : null;
  }, [userPos, trucks]);
  const outOfMarket = !loading && !loadError && !!userPos
    && nearestTruckMi != null && nearestTruckMi > 40;

  // In the list view we can reorder by distance when the user has location.
  // The toggle is hidden/meaningless without userPos. Sort is stable in time
  // within each distance tie (important for "Open Now" early returns).
  const byDistance = (a, b) => distanceFor(a) - distanceFor(b);
  const sortedForList = useMemo(() => {
    if (sortMode !== "distance" || !userPos) return sortedTrucks;
    return [...sortedTrucks].sort(byDistance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTrucks, sortMode, userPos]);

  // Per-event NWS weather forecasts. We feed the FILTERED+SORTED list
  // (not the raw weekly fetch) so changing the day toggle / category /
  // search updates the chips. Backend gates visibility via two
  // feature flags and returns {enabled:false} silently when off.
  const weather = useWeatherForecasts(sortedTrucks);
  const openNowSorted = useMemo(() => {
    if (sortMode !== "distance" || !userPos) return openNow;
    return [...openNow].sort(byDistance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNow, sortMode, userPos]);
  const laterTodaySorted = useMemo(() => {
    if (sortMode !== "distance" || !userPos) return laterToday;
    return [...laterToday].sort(byDistance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laterToday, sortMode, userPos]);

  const center = userPos ? [userPos.lat, userPos.lng] : [LANCASTER_COUNTY.lat, LANCASTER_COUNTY.lng];
  const initialZoom = userPos ? 13 : DEFAULT_ZOOM;

  // `?lat=&lng=&zoom=&label=` from the landing-page location search box.
  // When present, override the user-pos default so the customer lands
  // directly on the area they searched for (e.g. "Lititz, PA"). The
  // FlyToCoords component below performs the pan once the map is ready.
  const queryLat = parseFloat(searchParams.get("lat"));
  const queryLng = parseFloat(searchParams.get("lng"));
  const queryZoom = parseInt(searchParams.get("zoom") || "", 10);
  const queryLabel = searchParams.get("label");
  const hasQueryCoords =
    Number.isFinite(queryLat) && Number.isFinite(queryLng);
  const initialCenter = hasQueryCoords ? [queryLat, queryLng] : center;
  const startZoom = hasQueryCoords ? (Number.isFinite(queryZoom) ? queryZoom : 12) : initialZoom;

  return (
    <div className="relative h-[calc(100dvh-4rem-var(--safe-area-inset-top,env(safe-area-inset-top,0px))-var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px)))] w-full" data-testid="map-view">
      <PageMeta
        title="Live Food Truck Map — See Who's Serving Today | TruckTrackr"
        description="Interactive live map of food trucks open today. Tap a pin for hours, menu, and one-tap directions to the truck."
        canonical="https://thetrucktrackr.com/map"
      />
      {/* MAP MODE */}
      {viewMode === "map" && (
        <MapContainer
          center={initialCenter}
          zoom={startZoom}
          zoomControl={true}
          className="absolute inset-0 z-0"
        >
          <ThemeTiles />
          <MapCapture onMap={setMapInstance} />
          <ZoomWatcher onZoom={setMapZoom} />
          <FlyToSelected target={selected} />
          <InitialUserRecenter
            userPos={userPos}
            skip={!!requestedTruckId || !!selected || hasQueryCoords}
          />
          {hasQueryCoords && queryLabel && (
            // Floating chip telling the customer "you searched for X" —
            // a tiny cue that the map is no longer auto-centered on
            // their device location.
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] pointer-events-none inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-card/95 border border-border backdrop-blur shadow-sm text-xs font-semibold max-w-[calc(100%-32px)] truncate"
              data-testid="map-location-chip"
            >
              <span aria-hidden>📍</span> {queryLabel}
            </div>
          )}
          {userPos && (
            <Marker
              position={[userPos.lat, userPos.lng]}
              icon={uIcon}
              keyboard={false}
              interactive={false}
              zIndexOffset={1000}
            />
          )}
          {mapPins.map((t) => {
            if (suppressedTruckIds.has(t.id)) return null;
            const status = timeStatus(t);
            // Float live pins above future, future above past so stacked
            // markers at the same spot always let customers click the most
            // actionable one first. Boosted pins always float to the top.
            let z = status === "live" ? 500 : status === "future" ? 200 : 50;
            if (t.is_boosted) z += 1000;
            return (
              <Marker
                key={t.id}
                position={[t._lat, t._lng]}
                icon={truckIcon(t.food_category || "default", status, t.is_boosted, t.logo_pin_url, t.logo_pin_pos)}
                zIndexOffset={z}
                eventHandlers={{
                  click: () => {
                    trackAnalytics("pin_click", t.truck_id || t.id);
                    if (status === "past") {
                      trackAnalytics("pin_click_ended", t.truck_id || t.id);
                    }
                    pushPrompt.markInteraction();
                    setSelected(t);
                  },
                }}
              />
            );
          })}
          {/* Hotspot markers — single 🔥 circle per gathering, rendered
              above all truck pins (zIndexOffset 2000). Tap opens the
              hotspot sheet listing every participating truck. */}
          {activeHotspots.map((h) => (
            <Marker
              key={h.id}
              position={[h.lat, h.lng]}
              icon={hotspotIcon(h.tier, h.visible_count || h.count, hotspotAnimEnabled)}
              zIndexOffset={2000}
              eventHandlers={{
                click: () => {
                  trackAnalytics("hotspot_click", h.id);
                  setSelected(null);
                  setSelectedHotspot(h);
                },
              }}
            />
          ))}
        </MapContainer>
      )}

      {/* "Today across the county" weather chip — anchored bottom-LEFT
          above the PIN COLORS legend so they stack cleanly instead of
          colliding (legend lives at `bottom-5 left-3`; chip clears it
          at `bottom-32 sm:bottom-40`).
          Hidden when a truck detail sheet is open (`selected != null`)
          because the chip was overlapping the sheet's primary
          "Directions" CTA on mobile. */}
      {viewMode === "map" && weather.enabled && weather.header && !selected && (
        <div
          className="pointer-events-none absolute bottom-32 sm:bottom-40 left-3 z-[1000]"
          data-testid="weather-header-wrap"
        >
          <div className="pointer-events-auto">
            <WeatherChip forecast={weather.header} variant="header" />
          </div>
        </div>
      )}

      {/* Nearby-count banner (map mode only, only when user location + nearby trucks) */}
      {viewMode === "map" && userPos && thisWeekCount > 0 && (
        <div
          className="pointer-events-none absolute top-16 left-3 right-3 z-10 flex justify-center"
          data-testid="nearby-banner-wrap"
        >
          <div
            className="pointer-events-auto rounded-full bg-card/95 backdrop-blur-xl border border-border shadow-sm px-4 h-9 flex items-center gap-2 text-sm font-semibold"
            data-testid="nearby-banner"
          >
            <Flame weight="fill" className="h-4 w-4 text-primary" />
            {openNowCount > 0 ? (
              <span>
                <span className="text-primary">{openNowCount} open now</span>
                <span className="text-muted-foreground font-medium"> • {thisWeekCount} this week near you</span>
              </span>
            ) : (
              <span>
                {thisWeekCount} truck{thisWeekCount === 1 ? "" : "s"} this week{" "}
                <span className="text-muted-foreground font-medium">near you</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recenter / Use-my-location button (map mode only).
          Renders in TWO visual states:
            - No userPos yet → outlined crosshair = "tap to enable location"
            - userPos set    → filled crosshair  = "recenter on me"
          Critically, this button must render BEFORE we have userPos —
          otherwise iOS Chrome / Safari visitors get a chicken-and-egg
          (no button → no gesture → no prompt → no userPos → no button).
          The recenterOnUser handler internally calls
          getUserLocation({ requireInteraction: true }) which is the only
          path allowed to trigger the OS permission prompt. */}
      {viewMode === "map" && (
        <button
          type="button"
          onClick={recenterOnUser}
          className={
            "absolute right-3 bottom-5 sm:bottom-8 z-10 h-11 w-11 grid place-items-center rounded-full backdrop-blur-xl border shadow-sm active:scale-95 transition " +
            (userPos
              ? "bg-card/95 border-border hover:bg-card"
              : "bg-primary/95 border-primary/40 hover:bg-primary text-primary-foreground")
          }
          aria-label={userPos ? "Recenter on my location" : "Use my location"}
          title={userPos ? "Recenter on my location" : "Use my location"}
          data-testid="recenter-btn"
        >
          <Crosshair
            weight={userPos ? "duotone" : "fill"}
            className={"h-5 w-5 " + (userPos ? "text-primary" : "text-primary-foreground")}
          />
        </button>
      )}

      {/* Pin-color legend (map mode only) — small card so customers don't
          have to guess what black vs green vs orange means. Dismissible
          (persisted) for repeat users who've learned the code. */}
      {viewMode === "map" && !legendDismissed && (
        <div
          className="pointer-events-none absolute left-3 bottom-5 sm:bottom-8 z-10"
          data-testid="pin-legend-wrap"
        >
          <div
            className="pointer-events-auto relative rounded-2xl bg-card/98 backdrop-blur-xl border border-border shadow-lg px-3.5 py-2.5 text-xs sm:text-[13px]"
            data-testid="pin-legend"
          >
            <button
              type="button"
              onClick={() => { setLegendDismissed(true); try { localStorage.setItem("tt_legend_dismissed", "1"); } catch {} }}
              className="absolute top-1.5 right-1.5 h-5 w-5 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
              aria-label="Dismiss pin legend"
              data-testid="pin-legend-dismiss"
            >
              <X className="h-3 w-3" />
            </button>
            <div className="font-semibold text-foreground mb-2 tracking-wide uppercase text-[11px] pr-5">
              Pin Colors
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2.5">
                <span className="legend-dot legend-dot-live" aria-hidden="true" />
                <span className="font-medium">Live now</span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="legend-dot legend-dot-future" aria-hidden="true" />
                <span className="font-medium">Upcoming</span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="legend-dot legend-dot-past" aria-hidden="true" />
                <span className="font-medium text-muted-foreground">Ended (2h grace)</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Small "show legend" pill when dismissed — re-opens it */}
      {viewMode === "map" && legendDismissed && (
        <button
          type="button"
          onClick={() => { setLegendDismissed(false); try { localStorage.setItem("tt_legend_dismissed", "0"); } catch {} }}
          className="pointer-events-auto absolute left-3 bottom-5 sm:bottom-8 z-10 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-card/95 backdrop-blur-xl border border-border shadow-sm text-xs font-medium hover:bg-muted"
          data-testid="pin-legend-show"
          aria-label="Show pin color key"
        >
          <span className="legend-dot legend-dot-live" aria-hidden="true" style={{ width: 10, height: 10 }} />
          <span>Key</span>
        </button>
      )}

      {/* LIST MODE */}
      {viewMode === "list" && (
        <div
          className="absolute inset-0 z-0 overflow-y-auto bg-background"
          data-testid="list-view"
        >
          <div className="mx-auto max-w-2xl px-4 pt-20 pb-10 space-y-6">
            {filter === "followed" && sortedTrucks.length > 0 && (
              // Compact calendar-sync CTA for anonymous followers — only
              // surface on the followed-trucks list when they actually
              // have follows. Keeps the map UI clean for new visitors.
              <CalendarSyncCard compact />
            )}
            {userPos && sortedTrucks.length > 0 && (
              <div
                className="flex items-center justify-end"
                data-testid="sort-toggle-wrap"
              >
                <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setSortMode("time")}
                    className={`px-3 h-7 rounded-full font-medium transition ${
                      sortMode === "time" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="sort-time-btn"
                  >
                    Soonest
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortMode("distance")}
                    className={`px-3 h-7 rounded-full font-medium transition ${
                      sortMode === "distance" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="sort-distance-btn"
                  >
                    Closest
                  </button>
                </div>
              </div>
            )}
            {outOfMarket && sortedTrucks.length > 0 && (
              <div className="mb-4">
                <ExpansionWelcome
                  userPos={userPos}
                  onBrowseMarket={(m) => mapInstance?.flyTo([m.lat, m.lng], 11, { duration: 1.2 })}
                />
              </div>
            )}
            {loading ? (
              <div className="text-center text-muted-foreground py-10">Loading…</div>
            ) : loadError ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="list-load-error">
                <Storefront weight="duotone" className="h-10 w-10 mx-auto text-primary" />
                <h3 className="mt-3 font-display font-semibold text-xl">Couldn't load trucks</h3>
                <p className="text-sm text-muted-foreground mt-1">Give it another try — this is usually just a brief hiccup.</p>
                <button
                  onClick={() => { setLoading(true); setReloadKey((k) => k + 1); }}
                  className="mt-4 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
                  data-testid="list-retry-btn"
                >
                  Retry
                </button>
              </div>
            ) : sortedTrucks.length === 0 ? (
              <ExpansionWelcome
                userPos={userPos}
                onBrowseMarket={(m) => mapInstance?.flyTo([m.lat, m.lng], 11, { duration: 1.2 })}
              />
            ) : filter === "today" ? (
              <>
                <ListSection
                  title="Open Now"
                  accent
                  trucks={openNowSorted}
                  onSelect={(t) => {
                    setSelected(t);
                    setViewMode("map");
                  }}
                  isOpenNow={isOpenNow}
                  distanceFor={distanceFor}
                  emptyHint="No trucks currently serving. See what's starting soon below."
                  testId="section-open-now"
                  notifyTrucks={notifyTrucks}
                  onNotifyChange={setNotifyForTruck}
                  weatherById={weather.byId}
                />
                <ListSection
                  title="Later Today"
                  trucks={laterTodaySorted}
                  onSelect={(t) => {
                    setSelected(t);
                    setViewMode("map");
                  }}
                  isOpenNow={isOpenNow}
                  distanceFor={distanceFor}
                  emptyHint="Nothing else scheduled today."
                  testId="section-later-today"
                  notifyTrucks={notifyTrucks}
                  onNotifyChange={setNotifyForTruck}
                  weatherById={weather.byId}
                />
              </>
            ) : (
              <div className="space-y-3" data-testid="section-all">
                {sortedForList.map((t) => (
                  <TruckCard
                    key={t.id}
                    truck={t}
                    openNow={isOpenNow(t)}
                    distanceMi={distanceFor(t)}
                    onClick={() => {
                      setSelected(t);
                      setViewMode("map");
                    }}
                    isNotifySubscribed={notifyTrucks.has(t.truck_id || t.id)}
                    onNotifyChange={setNotifyForTruck}
                    weather={weather.byId.get(t.truck_id || t.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Floating header — single compact row.
          Layout (mobile-first):
            [Date selector] ........... [List/Map | Filters | Live? | count]
          The old multi-row design (filter pills + search bar + category
          chips) is now hidden behind two bottom sheets:
            • DateFilterPicker  → calendar-driven date selection
            • FiltersDrawer     → search / distance / hide-past / categories
          That keeps the header to a single ~52-px row so list-mode
          cards never get covered. */}
      <div className="pointer-events-none absolute top-3 left-3 right-3 z-10 flex items-center gap-2">
        <DateFilterPicker
          value={filter}
          onChange={(next) => setFilter(next)}
        />

        <div className="flex items-center gap-2 ml-auto pointer-events-auto">
          {/* List / Map toggle */}
          <div
            className="rounded-full border border-border bg-card/90 backdrop-blur-xl p-1 flex items-center gap-0.5 shadow-sm"
            data-testid="view-toggle"
          >
            <button
              onClick={() => { pushPrompt.markInteraction(); setViewMode("map"); }}
              className={`h-8 w-9 grid place-items-center rounded-full transition ${
                viewMode === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
              data-testid="view-map-btn"
              aria-label="Map view"
            >
              <MapTrifold weight="duotone" className="h-4 w-4" />
            </button>
            <button
              onClick={() => { pushPrompt.markInteraction(); setViewMode("list"); }}
              className={`h-8 w-9 grid place-items-center rounded-full transition ${
                viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
              data-testid="view-list-btn"
              aria-label="List view"
            >
              <ListBullets weight="duotone" className="h-4 w-4" />
            </button>
          </div>

          {/* Filters button removed Feb 2026 — the global navbar
              "Find your favorite truck" search now handles the
              search-by-name flow, and the cuisine chips below the
              header still let customers narrow by category. Removed
              per user feedback to clean up the map header. */}

          {/* Live-now chip — survives the redesign because it doubles as
              a quick "show me trucks open right now" CTA. Compact on
              mobile, full-text on desktop. */}
          {!loading && liveCount > 0 && (
            <button
              type="button"
              onClick={() => {
                if (filter !== "today") setFilter("today");
                if (viewMode !== "list") setViewMode("list");
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="section-open-now"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 120);
              }}
              className="rounded-full border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 px-2.5 sm:px-3 h-8 sm:h-9 inline-flex items-center gap-1.5 text-xs sm:text-sm shadow-sm transition-colors"
              data-testid="live-count-chip"
              aria-label={`${liveCount} trucks live right now`}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                {liveCount}<span className="hidden sm:inline ml-1">live</span>
              </span>
            </button>
          )}

          {/* Truck count chip — the "is anything happening?" indicator.
              Hidden when live chip is showing on mobile to keep the row
              uncluttered. */}
          <div
            className={
              "rounded-full border border-border bg-card/90 backdrop-blur-xl px-2.5 sm:px-3 h-8 sm:h-9 flex items-center gap-1.5 text-xs sm:text-sm shadow-sm " +
              (liveCount > 0 ? "hidden sm:flex" : "")
            }
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-medium" data-testid="truck-count">
              {loading ? "…" : sortedTrucks.length}
              <span className="hidden sm:inline ml-1">truck{sortedTrucks.length === 1 ? "" : "s"}</span>
            </span>
          </div>

          {/* Directory CTA — surfaces /profiles, pre-filtered to active
              trucks. Secondary to the live-map experience so it sits
              on the right and shrinks on mobile. */}
          <Link
            to="/profiles?active=1"
            onClick={() => trackAnalytics("directory_cta_click", null, { source: "map_toolbar" })}
            className="ml-auto sm:ml-0 rounded-full border border-border bg-card/90 backdrop-blur-xl hover:bg-muted px-2.5 sm:px-3 h-8 sm:h-9 inline-flex items-center gap-1.5 text-xs sm:text-sm shadow-sm transition-colors"
            data-testid="map-directory-cta"
          >
            <Storefront weight="duotone" className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold">
              <span className="hidden sm:inline">See all active</span>
              <span className="sm:hidden">All trucks</span>
              {activeProfileCount != null && <span className="ml-1 tabular-nums">({activeProfileCount})</span>}
            </span>
          </Link>
        </div>
      </div>

      {/* Filters bottom sheet (search / distance / hide-past / categories) */}
      <FiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        nearMeButton={
          <NearMeButton
            userPos={userPos}
            value={maxDistanceMi}
            onChange={setMaxDistanceMi}
          />
        }
        hidePast={hidePast}
        onHidePastChange={setHidePast}
        categories={categoryMeta.shouldShow ? categoryMeta.viable : []}
        activeCategory={categoryFilter}
        onCategoryChange={setCategoryFilter}
        followedActive={filter === "followed"}
        followedCount={followedCount}
        onToggleFollowed={() => setFilter(filter === "followed" ? "today" : "followed")}
        activeCount={activeFilterCount}
        onClearAll={() => {
          setSearchQuery("");
          setMaxDistanceMi(null);
          setHidePast(true);
          setCategoryFilter("");
          if (filter === "followed") setFilter("today");
        }}
      />

      {/* Feed failed (server blip) — retry card instead of a misleading
          "No trucks posted yet". */}
      {viewMode === "map" && !loading && loadError && (
        <div
          className="absolute inset-x-3 top-28 sm:top-24 z-10 mx-auto max-w-md rounded-2xl border border-border bg-card/95 backdrop-blur-xl p-5 text-center"
          data-testid="map-load-error"
        >
          <CalendarBlank weight="duotone" className="h-8 w-8 mx-auto text-primary" />
          <h3 className="mt-2 font-display font-semibold">Couldn't load trucks</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Give it another try — this is usually just a brief hiccup.
          </p>
          <button
            onClick={() => { setLoading(true); setReloadKey((k) => k + 1); }}
            className="mt-3 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
            data-testid="map-retry-btn"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state for map mode */}
      {viewMode === "map" && !loading && !loadError && sortedTrucks.length === 0 && (
        <div
          className="absolute inset-x-3 top-28 sm:top-24 z-10 mx-auto max-w-md rounded-2xl border border-border bg-card/95 backdrop-blur-xl p-5 text-center"
          data-testid="map-empty"
        >
          <CalendarBlank weight="duotone" className="h-8 w-8 mx-auto text-primary" />
          <h3 className="mt-2 font-display font-semibold">
            {filter === "followed"
              ? "You're not following any trucks yet"
              : categoryFilter || searchQuery || maxDistanceMi != null || hidePast
              ? "No matches"
              : "No trucks posted yet"}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {filter === "followed"
              ? "Tap the ⭐ Follow button on any truck to see them here. We'll also send you a Sunday heads-up so you don't miss a stop."
              : categoryFilter || searchQuery || maxDistanceMi != null || hidePast
              ? filter !== "week"
                ? "Nothing matches here. Try widening to This Week."
                : "Try clearing filters or picking a different category."
              : "Check back soon."}
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            {filter === "followed" && (
              <button
                type="button"
                onClick={() => setFilter("week")}
                className="rounded-full bg-primary text-primary-foreground text-sm px-4 py-2 font-semibold"
                data-testid="followed-empty-browse"
              >
                Browse trucks this week
              </button>
            )}
            {(categoryFilter || searchQuery || maxDistanceMi != null || hidePast) && filter !== "week" && filter !== "followed" && (
              <button
                type="button"
                onClick={() => setFilter("week")}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="map-empty-widen-week"
              >
                Try This Week
              </button>
            )}
            {(categoryFilter || searchQuery || maxDistanceMi != null || hidePast) && (
              <button
                type="button"
                onClick={() => { setCategoryFilter(""); setSearchQuery(""); setMaxDistanceMi(null); setHidePast(false); }}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium border border-border hover:bg-muted"
                data-testid="map-empty-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 300 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120) setSelected(null);
            }}
            className="absolute bottom-0 left-0 right-0 z-20 sm:left-4 sm:bottom-4 sm:right-auto sm:max-w-md rounded-t-3xl sm:rounded-2xl border border-white/10 bg-black/85 text-white backdrop-blur-2xl p-6 pt-4 shadow-2xl"
            data-testid="truck-detail-sheet"
          >
            <div className="mx-auto w-10 h-1 rounded-full bg-white/25 mb-4 sm:hidden" />

            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-[1.05]" data-testid="sheet-truck-name">
                    {selected.truck_name}
                  </h3>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-primary bg-primary/20 rounded-full px-2.5 py-1 font-semibold">
                    {dayLabel(selected.starts_at)}
                  </span>
                  {selected.founding_city_slug && selected.founding_slot_number ? (
                    <FoundingCityBadge
                      citySlug={selected.founding_city_slug}
                      slotNumber={selected.founding_slot_number}
                      size="sm"
                    />
                  ) : selected.owner_is_founding_pro && (
                    <FoundingProBadge size="sm" />
                  )}
                  {selected.is_boosted && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white bg-amber-500 rounded-full px-2.5 py-1 font-semibold"
                      data-testid="sheet-boosted-chip"
                      title="Featured boost"
                    >
                      ⚡ Boosted
                    </span>
                  )}
                  {selected.is_trending && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white bg-gradient-to-r from-rose-500 to-orange-500 rounded-full px-2.5 py-1 font-bold shadow-sm"
                      data-testid="sheet-trending-chip"
                      title="Trending — getting lots of shares this week"
                    >
                      🔥 Trending
                    </span>
                  )}
                  {selected.owner_is_pro && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white bg-gradient-to-r from-primary to-orange-500 rounded-full px-2.5 py-1 font-bold shadow-sm"
                      data-testid="sheet-pro-chip"
                      title="TruckTrackr Pro"
                    >
                      <Sparkle weight="fill" className="h-3 w-3" />
                      Pro
                    </span>
                  )}
                  <FollowButton
                    truckId={selected.truck_id || selected.id}
                    truckName={selected.truck_name}
                    compact={false}
                  />
                  <NotifyMeBell
                    truckId={selected.truck_id || selected.id}
                    truckName={selected.truck_name}
                    subscribed={notifyTrucks.has(selected.truck_id || selected.id)}
                    onChange={(next) => setNotifyForTruck(selected.truck_id || selected.id, next)}
                  />
                  <ShareButton
                    truckId={selected.truck_id || selected.id}
                    truckName={selected.truck_name}
                    compact={false}
                  />
                </div>
                {selected.event_name?.trim() && (
                  <div className="mt-2 text-lg font-semibold text-white/90 leading-snug" data-testid="sheet-event-subname">
                    {selected.event_name.trim()}
                  </div>
                )}
                <div className="mt-4 text-lg font-semibold text-white flex items-center gap-2 flex-wrap" data-testid="sheet-time">
                  <Clock weight="fill" className="h-5 w-5 text-primary flex-none" />
                  <span>{timeStr(selected.starts_at)} — {timeStr(selected.ends_at)}</span>
                  {/* Weather chip for the selected truck on the bottom
                      sheet — same gate as the card, just rendered here
                      because the sheet is a separate component path. */}
                  {weather.byId.get(selected.truck_id || selected.id) && (
                    <WeatherChip
                      forecast={weather.byId.get(selected.truck_id || selected.id)}
                      variant="card"
                    />
                  )}
                </div>
                <div className="mt-2.5 text-base text-white/85 flex items-start gap-2" data-testid="sheet-address">
                  <MapPin weight="fill" className="h-5 w-5 mt-0.5 text-primary flex-none" />
                  <span className="leading-snug">{selected.address}</span>
                </div>
                {/* Event Canceled banner (Feb 2026).
                    Placed directly under the address — the spot where
                    the customer's eyes are after reading "what + where
                    + when". Surfaces the reason without burying the
                    truck name or the action buttons above. The pin
                    on the map already paints red, so by the time the
                    customer opens the sheet they're primed for the
                    why; this answers it without front-loading. */}
                {selected.is_canceled && (() => {
                  const map = {
                    weather: "Weather",
                    mechanical: "Mechanical Issue",
                    staffing: "Staffing Shortage",
                    organizer: "Event Organizer Canceled",
                    personal: "Personal Emergency",
                  };
                  const reasonText = selected.cancel_reason === "other"
                    ? (selected.cancel_reason_custom || "Other")
                    : (map[selected.cancel_reason] || null);
                  return (
                    <div
                      className="mt-3 rounded-2xl border-2 border-rose-400 bg-rose-500/15 px-4 py-3"
                      data-testid="sheet-canceled-banner"
                      role="status"
                    >
                      <div className="flex items-start gap-3">
                        <span aria-hidden="true" className="text-2xl leading-none">🚫</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-extrabold tracking-widest uppercase text-rose-200">
                            Event Canceled
                          </div>
                          {reasonText && (
                            <div className="mt-1 text-sm text-rose-100">
                              <span className="text-rose-300/80">Reason:</span>{" "}
                              <span className="font-semibold">{reasonText}</span>
                            </div>
                          )}
                          <div className="mt-1.5 text-xs text-rose-100/80">
                            This event has been canceled. The truck won&rsquo;t be
                            serving here today.
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-white/70 hover:text-white hover:bg-white/10 rounded-full p-2 flex-none transition"
                aria-label="Close"
                data-testid="sheet-close-btn"
              >
                <X weight="bold" className="h-5 w-5" />
              </button>
            </div>

            {selected.menu_text && (
              <p className="mt-5 whitespace-pre-wrap text-base leading-relaxed text-white/90">{selected.menu_text}</p>
            )}

            {(() => {
              const imgs = Array.isArray(selected.menu_image_paths) && selected.menu_image_paths.length
                ? selected.menu_image_paths
                : (selected.menu_image_path ? [selected.menu_image_path] : []);
              if (imgs.length === 0) return null;
              return (
                <MenuImagesCarousel
                  paths={imgs}
                  onOpen={(idx) => {
                    trackAnalytics("menu_view", selected.truck_id || selected.id);
                    setLightbox({ paths: imgs, startIndex: idx });
                  }}
                />
              );
            })()}

            <div className="mt-5 w-full" data-testid="sheet-directions-wrapper">
              {selected.is_canceled ? (
                <div
                  className="w-full text-center rounded-full border border-white/15 bg-white/5 py-3 text-sm text-white/60"
                  data-testid="sheet-directions-canceled-stub"
                  aria-disabled="true"
                  role="note"
                >
                  Directions disabled — event canceled
                </div>
              ) : (
                <DirectionsButton
                  lat={selected.lat}
                  lng={selected.lng}
                  address={selected.address}
                  label={selected.truck_name || selected.name}
                  variant="filled"
                  testId="sheet-directions-link"
                  trackOpen={() => trackAnalytics("directions_click", selected.truck_id || selected.id)}
                />
              )}
            </div>
            {/* Order Online + Contact CTAs — Pro-only, surfaced only when
                the owner has actually configured them. Hidden cleanly
                for non-Pro / unconfigured trucks so there's no
                disabled-button noise.

                Layout: two side-by-side compact buttons when both are
                available; otherwise a single full-width button. */}
            {(selected.ordering_url || (selected.truck_slug && selected.owner_is_pro)) && (
              <div
                className={`mt-2.5 grid gap-2 ${selected.ordering_url && selected.truck_slug && selected.owner_is_pro ? "grid-cols-2" : "grid-cols-1"}`}
                data-testid="sheet-pro-actions"
              >
                {selected.ordering_url && (
                  <a
                    href={selected.ordering_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackAnalytics("ordering_click", selected.truck_id || selected.id, {
                      truck_name: selected.truck_name,
                      source_card_type: "map_sheet",
                    })}
                    className="h-11 rounded-full bg-white text-primary font-bold text-sm hover:bg-white/90 transition flex items-center justify-center gap-2"
                    data-testid="sheet-order-online-link"
                  >
                    <ShoppingBag weight="duotone" className="h-4 w-4" />
                    {selected.ordering_label || "Order Online"}
                    <ArrowSquareOut className="h-3.5 w-3.5 opacity-70" />
                  </a>
                )}
                {selected.truck_slug && selected.owner_is_pro && (
                  <ContactTruckButton
                    slug={selected.truck_slug}
                    truckName={selected.truck_name}
                    variant="sheet"
                    onOpen={() => trackAnalytics("contact_click", selected.truck_id || selected.id, {
                      truck_name: selected.truck_name,
                      source_card_type: "map_sheet",
                    })}
                  />
                )}
              </div>
            )}
            {selected.truck_slug && (
              <Link
                to={`/t/${selected.truck_slug}`}
                onClick={() => {
                  trackAnalytics("truck_profile_cta_click", selected.truck_id || selected.id, {
                    truck_name: selected.truck_name,
                    source_card_type: "map_sheet",
                    is_pro: !!selected.owner_is_pro,
                  });
                }}
                className="mt-2.5 flex items-center justify-center gap-2 rounded-full border border-white/20 text-white hover:bg-white/10 font-semibold text-sm h-11 transition"
                data-testid="sheet-view-profile-link"
              >
                <Storefront weight="duotone" className="h-4 w-4" />
                View profile
                <ArrowSquareOut className="h-3.5 w-3.5 opacity-70" />
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu image lightbox — full-screen, swipeable when multiple */}
      <AnimatePresence>
        {lightbox && (
          <MenuImagesLightbox
            paths={lightbox.paths}
            startIndex={lightbox.startIndex}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>

      {/* Hotspot detail bottom-sheet — list of trucks gathered at one venue */}
      <HotspotSheet
        hotspot={selectedHotspot}
        onClose={() => setSelectedHotspot(null)}
        onTruckTap={(t) => {
          // Resolve to the full truck record from the current list so the
          // existing truck-detail sheet gets the same shape as a pin click.
          const full = trucks.find((x) => x.id === t.id) || t;
          setSelectedHotspot(null);
          setSelected(full);
        }}
      />

      {/* Phase B push opt-in soft prompt — auto-hides while a truck
          detail sheet is open so it never overlaps the primary action. */}
      <PushOptInPrompt visible={promptVisible} onDismiss={pushPrompt.dismiss} />

      {/* Contextual signin nudge — fires once per session for an
          anonymous user when a followed truck is currently live. */}
      <LoginPromptModal
        open={!!contextualSigninTruck}
        onClose={() => setContextualSigninTruck(null)}
        title={`${contextualSigninTruck?.truck_name || "Your favorite truck"} is open right now`}
        body="Sign in so we can ping you the moment it opens next time. Your follows stay synced across devices too."
      />
    </div>
  );
}
